'use client';

import React from 'react';
import Link from 'next/link';
import { useSiteMapContext } from './site_map_context';

export type SiteMapListing = {
  id: string;
  title: string | null | undefined;
  bedrooms: number | null | undefined;
  max_guests: number | null | undefined;
  href: string;
};

type SiteMapTableProps = {
  listings: SiteMapListing[];
  nameColumnLabel?: string;
};

function HighlightedText({
  text,
  highlight,
}: {
  text: string;
  highlight: string;
}) {
  if (!highlight.trim()) {
    return <>{text}</>;
  }

  const regex = new RegExp(`(${highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);

  return (
    <>
      {parts.map((part, index) =>
        regex.test(part) ? (
          <span key={index} className="font-semibold text-link">
            {part}
          </span>
        ) : (
          <span key={index} className="text-link">{part}</span>
        )
      )}
    </>
  );
}

export function SiteMapTable({
  listings,
  nameColumnLabel = 'Unit Name',
}: SiteMapTableProps) {
  const { searchQuery } = useSiteMapContext();
  const trimmedQuery = searchQuery.trim();

  const filteredListings = trimmedQuery
    ? listings.filter((listing) =>
        listing.title?.toLowerCase().includes(trimmedQuery.toLowerCase())
      )
    : listings;

  return (
    <div className="grid w-full min-w-0 grid-cols-12">
      <div className="col-span-4 min-w-0 bg-black-1 px-2 py-3 text-xs font-bold text-white sm:col-span-6 sm:text-base md:col-span-8">
        {nameColumnLabel}
      </div>
      <div className="col-span-4 min-w-0 bg-black-1 px-2 py-3 text-center text-xs font-bold text-white sm:col-span-3 sm:text-base md:col-span-2">
        Bedrooms
      </div>
      <div className="col-span-4 min-w-0 bg-black-1 px-2 py-3 text-center text-xs font-bold text-white sm:col-span-3 sm:text-base md:col-span-2">
        Occupancy
      </div>
      {filteredListings.map((listing, index) => (
        <React.Fragment key={listing.id}>
          <div
            className={`col-span-4 min-w-0 border-b border-b-gray-4 px-2 py-3 text-sm sm:col-span-6 sm:text-base md:col-span-8 ${index % 2 === 1 ? 'bg-gray-1' : ''
              }`}
          >
            <Link href={listing.href} className="text-link" prefetch={false}>
              {listing.title && trimmedQuery ? (
                <HighlightedText text={listing.title} highlight={trimmedQuery} />
              ) : (
                listing.title
              )}
            </Link>
          </div>
          <div
            className={`col-span-4 min-w-0 border-b border-b-gray-4 px-2 py-3 text-center text-sm sm:col-span-3 sm:text-base md:col-span-2 ${index % 2 === 1 ? 'bg-gray-1' : ''
              }`}
          >
            {listing.bedrooms}
          </div>
          <div
            className={`col-span-4 min-w-0 border-b border-b-gray-4 px-2 py-3 text-center text-sm sm:col-span-3 sm:text-base md:col-span-2 ${index % 2 === 1 ? 'bg-gray-1' : ''
              }`}
          >
            {listing.max_guests}
          </div>
        </React.Fragment>
      ))}
      {filteredListings.length === 0 && searchQuery !== '' && (
        <div className="col-span-12 text-center text-base text-gray-500 mt-10">
          <p className="flex items-center justify-center h-full font-medium">
            Nothing matching your search for &quot;{trimmedQuery || searchQuery}&quot;
          </p>
          <p className="text-sm text-gray-500">Try searching for a different keyword.</p>
        </div>
      )}
      {filteredListings.length === 0 && searchQuery === '' && (
        <div className="col-span-12 text-center text-base text-gray-500 mt-10">
          <p className="flex items-center justify-center h-full font-medium">
            No listings available.
          </p>
        </div>
      )}
    </div>
  );
}
